import {
    type AgentAutomationCapabilitiesRequest,
    type AgentAutomationEventTriggerCatalogItem,
    type AgentAutomationEventTriggerRead,
    type AgentAutomationRunRead,
    type AgentAutomationType,
    type AutomationFormState,
    type AutomationGroupDefinitionRead,
    type AutomationScheduleConfig,
    type CustomScheduleUnit,
    type EventEntityType,
    type EventTriggerFormState,
    type EventTriggerKind,
    type OutreachStatus,
    type PhoebeEventTriggerConfig,
    type ScheduleFormState,
    type ScheduleKind,
    type ShiftStatus,
} from '@apps/web/components/automations/types';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { formatTimezoneLabel } from '@apps/web/lib/timezone_labels';
import { WEEKDAYS } from '@apps/web/lib/weekdays';

export const DEFAULT_SCHEDULE_FORM: ScheduleFormState = {
    kind: 'daily',
    minute: 0,
    time: '09:00',
    weekday: 0,
    interval: 1,
    unit: 'days',
};

export const DEFAULT_FORM: AutomationFormState = {
    name: '',
    automationType: 'user',
    instructions: '',
    description: '',
    capabilities: {
        slack: {
            enabled: false,
            channelId: null,
        },
        model: null,
    },
    status: 'active',
    schedule: null,
    eventTriggers: [],
    eventTriggerOperator: 'or',
};

export const AUTOMATION_PAGE_SIZE = 50;

export const AUTOMATION_TYPE_OPTIONS: ReadonlyArray<{
    readonly value: AgentAutomationType;
    readonly label: string;
}> = [
    { value: 'user', label: 'User' },
    { value: 'system', label: 'System' },
    { value: 'shift_cancellations', label: 'Shift cancellations' },
    { value: 'auto_draft', label: 'Auto draft' },
    { value: 'open_shift', label: 'Open shift' },
] as const;

export function automationTypeLabel(type: AgentAutomationType): string {
    return (
        AUTOMATION_TYPE_OPTIONS.find((option) => option.value === type)
            ?.label ?? humanizeStatus(type)
    );
}

export const SHIFT_STATUS_OPTIONS: ReadonlyArray<ShiftStatus> = [
    'open',
    'assigned',
    'assigned_and_confirmed',
    'pending_confirmation',
    'completed',
    'cancelled',
    'missing',
    'in_progress',
    'needs_attention',
    'not_scheduled',
    'unknown',
] as const;
export const DEFAULT_SHIFT_STATUS: ShiftStatus = 'open';

export const OUTREACH_STATUS_OPTIONS: ReadonlyArray<OutreachStatus> = [
    'pending',
    'paused',
    'in_progress',
    'filled',
    'unfilled',
    'cancelled',
] as const;
export const DEFAULT_OUTREACH_STATUS: OutreachStatus = 'pending';

export function humanizeStatus(value: string): string {
    return value
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

export type TriggerStatusOption = {
    readonly value: string;
    readonly label: string;
};

export type TriggerKindOption = {
    readonly value: EventTriggerKind;
    readonly label: string;
};

export function triggerKindOptionsForEntity(
    catalogItems: ReadonlyArray<AgentAutomationEventTriggerCatalogItem>,
    entityType: EventEntityType,
): ReadonlyArray<TriggerKindOption> {
    const catalogOptions = catalogItems
        .filter((item) => item.entity_type === entityType)
        .map((item): TriggerKindOption => ({
            value: item.trigger_kind,
            label: item.label,
        }));
    if (catalogOptions.length > 0) {
        return catalogOptions;
    }
    const createdOption: TriggerKindOption = { value: 'created', label: 'New' };
    const statusChangedOption: TriggerKindOption = {
        value: 'status_changed',
        label: 'Status change',
    };
    if (entityType === 'shift') {
        return [
            createdOption,
            { value: 'caregiver_cancelled', label: 'Caregiver cancelled' },
            statusChangedOption,
        ];
    }
    return [createdOption, statusChangedOption];
}

function defaultTriggerKindForEntity(
    catalogItems: ReadonlyArray<AgentAutomationEventTriggerCatalogItem>,
    entityType: EventEntityType,
): EventTriggerKind {
    return (
        triggerKindOptionsForEntity(catalogItems, entityType)[0]?.value ??
        'created'
    );
}

export function statusOptionsForEntity(
    catalogItems: ReadonlyArray<AgentAutomationEventTriggerCatalogItem>,
    entityType: EventEntityType,
): ReadonlyArray<TriggerStatusOption> {
    const catalogItem = catalogItems.find(
        (item) =>
            item.entity_type === entityType &&
            item.trigger_kind === 'status_changed',
    );
    const catalogOptions = catalogItem?.new_status_options ?? [];
    if (catalogOptions.length > 0) {
        return catalogOptions;
    }
    const fallback =
        entityType === 'shift' ? SHIFT_STATUS_OPTIONS : OUTREACH_STATUS_OPTIONS;
    return fallback.map((status) => ({
        value: status,
        label: humanizeStatus(status),
    }));
}

export function defaultStatusForEntity(
    catalogItems: ReadonlyArray<AgentAutomationEventTriggerCatalogItem>,
    entityType: EventEntityType,
): string {
    return (
        statusOptionsForEntity(catalogItems, entityType)[0]?.value ??
        (entityType === 'shift'
            ? DEFAULT_SHIFT_STATUS
            : DEFAULT_OUTREACH_STATUS)
    );
}

export function eventNameFromForm(
    trigger: EventTriggerFormState,
): PhoebeEventTriggerConfig['event_name'] {
    if (trigger.entityType === 'shift') {
        if (trigger.triggerKind === 'created') {
            return 'shift.created';
        }
        if (trigger.triggerKind === 'caregiver_cancelled') {
            return 'shift.caregiver_cancelled';
        }
        return 'shift.status_changed';
    }
    return trigger.triggerKind === 'created'
        ? 'outreach.created'
        : 'outreach.status_changed';
}

export function defaultSchedule(kind: ScheduleKind): ScheduleFormState {
    return {
        ...DEFAULT_SCHEDULE_FORM,
        kind,
        unit: kind === 'custom' ? 'days' : DEFAULT_SCHEDULE_FORM.unit,
    };
}

// Compute the patch that should accompany a Frequency dropdown change.
// `buildScheduleConfig` only reads `unit`/`interval` for `custom` kind, so a
// switch from weekly/daily/hourly into `custom` needs to seed `unit` and
// `interval` to a value that mirrors the previous cadence. Without this,
// switching weekly → custom would silently save as `every 1 day`.
export function applyScheduleKindChange(
    schedule: ScheduleFormState,
    nextKind: ScheduleKind,
): Partial<ScheduleFormState> {
    if (nextKind !== 'custom' || schedule.kind === 'custom') {
        return { kind: nextKind };
    }
    const unit: CustomScheduleUnit =
        schedule.kind === 'weekly'
            ? 'weeks'
            : schedule.kind === 'hourly'
              ? 'hours'
              : 'days';
    return { kind: nextKind, unit, interval: 1 };
}

export { WEEKDAYS } from '@apps/web/lib/weekdays';

/**
 * The days a weekly schedule fires on, as sorted 0-6 (Monday=0).
 *
 * Weekly schedules can name several days ("every weekday at 7am" is one
 * schedule, not five), which is what `weekdays` holds. The scalar `weekday`
 * predates it and still appears in configs written before the change.
 */
export function scheduleWeekdays(
    config: Record<string, unknown>,
): Array<number> {
    const weekdays = config['weekdays'];
    if (Array.isArray(weekdays)) {
        const days = weekdays.filter(
            (day): day is number =>
                typeof day === 'number' && day >= 0 && day <= 6,
        );
        if (days.length > 0) {
            return [...new Set(days)].sort((a, b) => a - b);
        }
    }
    return [readNumber(config['weekday'], 0)];
}

/**
 * Weekdays as a readable list: the two common shapes get their shorthand, and
 * anything else is spelled out day by day.
 */
export function weekdaysLabel(weekdays: ReadonlyArray<number>): string {
    const days = [...weekdays].sort((a, b) => a - b);
    if (days.length === 7) {
        return 'Every day';
    }
    if (days.length === 5 && days.every((day) => day <= 4)) {
        return 'Every weekday';
    }
    if (days.length === 2 && days[0] === 5 && days[1] === 6) {
        return 'Every weekend day';
    }
    const names = days.map((day) => WEEKDAYS[day] ?? 'Monday');
    if (names.length === 1) {
        return `Every ${names[0]}`;
    }
    return `Every ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function readString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function readNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value)
        ? value
        : fallback;
}

export function clampInteger(value: number, min: number, max: number): number {
    if (!Number.isFinite(value)) {
        return min;
    }
    return Math.min(max, Math.max(min, Math.trunc(value)));
}

export function numberFromInput(value: string, fallback: number): number {
    if (!value.trim()) {
        return fallback;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

export function buildScheduleConfig(
    schedule: ScheduleFormState,
): AutomationScheduleConfig {
    if (schedule.kind === 'hourly') {
        return {
            kind: 'hourly',
            minute: clampInteger(schedule.minute, 0, 59),
        };
    }
    if (schedule.kind === 'daily') {
        return { kind: 'daily', time: schedule.time };
    }
    if (schedule.kind === 'weekly') {
        return {
            kind: 'weekly',
            weekday: clampInteger(schedule.weekday, 0, 6),
            time: schedule.time,
        };
    }
    if (schedule.unit === 'minutes') {
        return {
            kind: 'custom',
            interval: clampInteger(schedule.interval, 1, 365),
            unit: 'minutes',
        };
    }
    if (schedule.unit === 'hours') {
        return {
            kind: 'custom',
            interval: clampInteger(schedule.interval, 1, 365),
            unit: 'hours',
            minute: clampInteger(schedule.minute, 0, 59),
        };
    }
    if (schedule.unit === 'days') {
        return {
            kind: 'custom',
            interval: clampInteger(schedule.interval, 1, 365),
            unit: 'days',
            time: schedule.time,
        };
    }
    return {
        kind: 'custom',
        interval: clampInteger(schedule.interval, 1, 365),
        unit: 'weeks',
        weekday: clampInteger(schedule.weekday, 0, 6),
        time: schedule.time,
    };
}

export const MAX_START_WITHIN_MINUTES = 5 * 24 * 60;

export function parseStartWithinMinutes(value: string): number | null {
    const trimmed = value.trim();
    if (trimmed === '') {
        return null;
    }
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
        return null;
    }
    return Math.min(parsed, MAX_START_WITHIN_MINUTES);
}

export function buildEventTriggers(
    triggers: ReadonlyArray<EventTriggerFormState>,
): Array<PhoebeEventTriggerConfig> {
    return triggers.map((trigger) => {
        const eventFilter: Record<string, string | number> =
            trigger.triggerKind === 'status_changed'
                ? { new_status: trigger.newStatus }
                : {};
        const startWithinMinutes = parseStartWithinMinutes(
            trigger.startWithinMinutes,
        );
        if (trigger.entityType === 'shift' && startWithinMinutes !== null) {
            eventFilter['start_within_minutes'] = startWithinMinutes;
        }
        // per_event is the default; only the batched choice is stored.
        if (trigger.dispatch === 'batched') {
            eventFilter['dispatch'] = 'batched';
        }
        return {
            entity_type: trigger.entityType,
            event_name: eventNameFromForm(trigger),
            event_filter: eventFilter,
        };
    });
}

export function buildCapabilities(
    capabilities: AutomationFormState['capabilities'],
): AgentAutomationCapabilitiesRequest {
    return {
        slack: {
            enabled: capabilities.slack.enabled,
            channel_id:
                capabilities.slack.enabled &&
                capabilities.slack.channelId !== null
                    ? capabilities.slack.channelId
                    : null,
        },
    };
}

export function defaultEventTrigger(
    {
        entityType = 'shift',
        triggerKind = 'created',
        newStatus,
    }: {
        readonly entityType?: EventEntityType;
        readonly triggerKind?: EventTriggerKind;
        readonly newStatus?: string;
    } = {},
    catalogItems: ReadonlyArray<AgentAutomationEventTriggerCatalogItem> = [],
): EventTriggerFormState {
    const fallbackStatus = defaultStatusForEntity(catalogItems, entityType);
    return {
        id:
            globalThis.crypto?.randomUUID?.() ??
            `trigger-${Date.now()}-${Math.random()}`,
        entityType,
        triggerKind,
        newStatus: newStatus ?? fallbackStatus,
        startWithinMinutes: '',
        dispatch: 'per_event',
    };
}

export function eventTriggerFormFromRead(
    trigger: AgentAutomationEventTriggerRead,
    index = 0,
): EventTriggerFormState {
    const entityType = trigger.entity_type;
    const triggerKind =
        trigger.event_name === 'shift.caregiver_cancelled'
            ? 'caregiver_cancelled'
            : trigger.event_name.endsWith('.status_changed')
              ? 'status_changed'
              : 'created';
    const fallbackStatus =
        entityType === 'shift' ? DEFAULT_SHIFT_STATUS : DEFAULT_OUTREACH_STATUS;
    const newStatus =
        typeof trigger.event_filter['new_status'] === 'string'
            ? trigger.event_filter['new_status']
            : fallbackStatus;
    const startWithinMinutes =
        typeof trigger.event_filter['start_within_minutes'] === 'number'
            ? String(trigger.event_filter['start_within_minutes'])
            : '';
    const dispatch =
        trigger.event_filter['dispatch'] === 'batched'
            ? 'batched'
            : 'per_event';
    return {
        // Event configs are group-owned values, not persisted rows. This
        // deterministic key keeps edit controls stable while the form is open.
        id: `${index}:${trigger.entity_type}:${trigger.event_name}:${JSON.stringify(trigger.event_filter)}`,
        entityType,
        triggerKind,
        newStatus,
        startWithinMinutes,
        dispatch,
    };
}

export function applyEventTriggerPatch(
    trigger: EventTriggerFormState,
    patch: Partial<EventTriggerFormState>,
    catalogItems: ReadonlyArray<AgentAutomationEventTriggerCatalogItem> = [],
): EventTriggerFormState {
    let next = { ...trigger, ...patch };
    // Start windows only apply to shift-entity triggers.
    if (next.entityType === 'outreach' && next.startWithinMinutes !== '') {
        next = { ...next, startWithinMinutes: '' };
    }
    const triggerKindOptions = triggerKindOptionsForEntity(
        catalogItems,
        next.entityType,
    ).map((option) => option.value);
    const options = statusOptionsForEntity(catalogItems, next.entityType).map(
        (option) => option.value,
    );
    if (!triggerKindOptions.includes(next.triggerKind)) {
        return {
            ...next,
            triggerKind: defaultTriggerKindForEntity(
                catalogItems,
                next.entityType,
            ),
            newStatus: defaultStatusForEntity(catalogItems, next.entityType),
        };
    }
    if (
        patch.triggerKind === 'status_changed' &&
        !options.includes(next.newStatus)
    ) {
        return {
            ...next,
            newStatus: defaultStatusForEntity(catalogItems, next.entityType),
        };
    }
    if (patch.entityType === 'shift' && !options.includes(next.newStatus)) {
        return {
            ...next,
            newStatus: defaultStatusForEntity(catalogItems, 'shift'),
        };
    }
    if (patch.entityType === 'outreach' && !options.includes(next.newStatus)) {
        return {
            ...next,
            newStatus: defaultStatusForEntity(catalogItems, 'outreach'),
        };
    }
    return next;
}

export function formFromAutomation(
    automation: AutomationGroupDefinitionRead | null,
): AutomationFormState {
    if (automation === null) {
        return DEFAULT_FORM;
    }
    const schedule = automation.schedule;
    const config = schedule?.config ?? {};
    const kind =
        schedule?.kind === 'hourly' ||
        schedule?.kind === 'daily' ||
        schedule?.kind === 'weekly' ||
        schedule?.kind === 'custom'
            ? schedule.kind
            : 'daily';
    const unit =
        schedule?.unit === 'minutes' ||
        schedule?.unit === 'hours' ||
        schedule?.unit === 'days' ||
        schedule?.unit === 'weeks'
            ? schedule.unit
            : 'days';

    return {
        name: automation.name,
        automationType: automation.automation_type,
        instructions: automation.instructions,
        description: automation.description ?? '',
        capabilities: {
            slack: {
                enabled: automation.capabilities.slack.enabled,
                channelId: automation.capabilities.slack.channel_id ?? null,
            },
            // Only the admin group read carries the pinned model.
            model:
                'model' in automation.capabilities
                    ? (automation.capabilities.model ?? null)
                    : null,
        },
        // The form's status field is the group master switch (edit PATCH
        // writes it onto the group), so seed from group_status — the
        // member's own status is per-organization state managed via
        // pause/resume, not this form.
        status: automation.group_status === 'paused' ? 'paused' : 'active',
        schedule:
            schedule === null
                ? null
                : {
                      kind,
                      unit,
                      minute: readNumber(
                          config['minute'],
                          DEFAULT_SCHEDULE_FORM.minute,
                      ),
                      time:
                          readString(config['time']) ??
                          DEFAULT_SCHEDULE_FORM.time,
                      weekday: readNumber(
                          config['weekday'],
                          DEFAULT_SCHEDULE_FORM.weekday,
                      ),
                      interval: readNumber(
                          config['interval'],
                          DEFAULT_SCHEDULE_FORM.interval,
                      ),
                  },
        eventTriggers: (automation.event_triggers ?? []).map(
            eventTriggerFormFromRead,
        ),
        eventTriggerOperator: automation.event_trigger_operator ?? 'or',
    };
}

export function scheduleLabelFromConfig(
    kind: string,
    unit: string | null,
    config: Record<string, unknown>,
    timezone?: string,
): string {
    const withTimezone = (label: string) =>
        timezone ? `${label} ${formatTimezoneLabel(timezone)}` : label;
    if (kind === 'hourly') {
        return withTimezone(
            `Hourly at minute ${readNumber(config['minute'], 0)}`,
        );
    }
    if (kind === 'daily') {
        return withTimezone(
            `Daily at ${readString(config['time']) ?? '09:00'}`,
        );
    }
    if (kind === 'weekly') {
        // A weekly schedule can name several days, so the label lists them all
        // rather than reporting only the first.
        return withTimezone(
            `${weekdaysLabel(scheduleWeekdays(config))} at ${
                readString(config['time']) ?? '09:00'
            }`,
        );
    }
    const interval = readNumber(config['interval'], 1);
    if (unit === 'minutes') {
        return `Every ${interval} ${interval === 1 ? 'minute' : 'minutes'}`;
    }
    if (unit === 'hours') {
        return withTimezone(
            `Every ${interval} ${interval === 1 ? 'hour' : 'hours'} at minute ${readNumber(config['minute'], 0)}`,
        );
    }
    if (unit === 'weeks') {
        const weekday = WEEKDAYS[readNumber(config['weekday'], 0)] ?? 'Monday';
        return withTimezone(
            `Every ${interval} ${interval === 1 ? 'week' : 'weeks'} on ${weekday} at ${readString(config['time']) ?? '09:00'}`,
        );
    }
    return withTimezone(
        `Every ${interval} ${interval === 1 ? 'day' : 'days'} at ${readString(config['time']) ?? '09:00'}`,
    );
}

export function scheduleLabel(
    automation: Pick<AutomationGroupDefinitionRead, 'schedule'>,
    timezone?: string,
): string {
    if (automation.schedule === null) {
        return 'No schedule';
    }
    return scheduleLabelFromConfig(
        automation.schedule.kind,
        automation.schedule.unit,
        automation.schedule.config,
        timezone,
    );
}

export function eventTriggerLabel(trigger: EventTriggerFormState): string {
    const entity = trigger.entityType === 'shift' ? 'Shift' : 'Outreach';
    const windowMinutes = parseStartWithinMinutes(trigger.startWithinMinutes);
    const windowSuffix =
        trigger.entityType === 'shift' && windowMinutes !== null
            ? ` (starts within ${windowMinutes} min)`
            : '';
    if (trigger.triggerKind === 'created') {
        return `${entity} new${windowSuffix}`;
    }
    if (trigger.triggerKind === 'caregiver_cancelled') {
        return `Shift caregiver cancelled${windowSuffix}`;
    }
    return `${entity} status: ${humanizeStatus(trigger.newStatus)}${windowSuffix}`;
}

export function automationRunTriggerLabel(
    run: Pick<AgentAutomationRunRead, 'fire_reason' | 'trigger_cause_label'>,
): string {
    const fireReason = humanizeStatus(run.fire_reason);
    if (run.fire_reason === 'event' && run.trigger_cause_label) {
        return `${fireReason} · ${run.trigger_cause_label}`;
    }
    return fireReason;
}

export function triggerSummary(
    automation: Pick<
        AutomationGroupDefinitionRead,
        'schedule' | 'event_triggers' | 'event_trigger_operator'
    >,
    timezone?: string,
): string {
    const scheduleLabels =
        automation.schedule === null
            ? []
            : [scheduleLabel(automation, timezone)];
    const eventLabels = (automation.event_triggers ?? []).map((trigger) =>
        eventTriggerLabel(eventTriggerFormFromRead(trigger)),
    );
    const labels = [...scheduleLabels, ...eventLabels];
    if (eventLabels.length > 1) {
        const operator =
            automation.event_trigger_operator === 'and' ? ' and ' : ' or ';
        return eventLabels.join(operator);
    }
    return labels.length > 0 ? labels.join(', ') : 'No triggers';
}

export function formatOptionalDate(
    value: string | null,
    timezone: string,
): string {
    return value ? formatDateAndTime(value, timezone) : 'Not scheduled';
}

export function canSaveAutomationForm(
    form: AutomationFormState,
    organizationId: string | null,
    isSaving: boolean,
    slackConnected: boolean | null,
    slackSelectedChannelAvailable: boolean | null = null,
): boolean {
    const hasSchedule = form.schedule !== null;
    const hasEvents = form.eventTriggers.length > 0;
    const slackValid =
        !form.capabilities.slack.enabled ||
        (form.capabilities.slack.channelId !== null &&
            slackConnected !== false &&
            slackSelectedChannelAvailable !== false);
    return (
        organizationId !== null &&
        form.name.trim().length > 0 &&
        form.instructions.trim().length > 0 &&
        slackValid &&
        hasSchedule !== hasEvents &&
        form.eventTriggers.every(
            (trigger) =>
                trigger.triggerKind === 'created' ||
                (trigger.entityType === 'shift' &&
                    trigger.triggerKind === 'caregiver_cancelled') ||
                (trigger.triggerKind === 'status_changed' &&
                    trigger.newStatus.trim().length > 0),
        ) &&
        !isSaving
    );
}
